import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(70);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Playlist state for auto-advance
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoMode, setIsAutoMode] = useState(false);

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume / 100;
    }
  }, []);

  // Fade out current audio and execute callback when done
  const fadeOutAndExecute = useCallback((callback, fadeDuration = 500) => {
    const audio = audioRef.current;
    if (!audio || audio.paused) {
      callback?.();
      return;
    }

    // Clear any existing fade interval
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
    }

    const startVolume = audio.volume;
    const steps = 20; // Number of steps for fade
    const stepDuration = fadeDuration / steps;
    const volumeStep = startVolume / steps;
    let currentStep = 0;

    fadeIntervalRef.current = setInterval(() => {
      currentStep++;
      const newVolume = Math.max(0, startVolume - (volumeStep * currentStep));
      audio.volume = newVolume;

      if (currentStep >= steps) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
        audio.pause();
        // Restore volume for next playback
        audio.volume = volume / 100;
        callback?.();
      }
    }, stepDuration);
  }, [volume]);

  // Play item at specific index with optional seek position
  const playItemAtIndex = useCallback((playlist, index, seekPosition = 0) => {
    if (!playlist || !playlist[index]) return;
    
    const item = playlist[index];
    const audio = audioRef.current;
    
    if (audio) {
      audio.src = item.file_path;
      audio.volume = volume / 100;
      audio.load();
      
      // If we need to seek to a specific position (for in-house radio behavior)
      if (seekPosition > 0) {
        audio.addEventListener('loadedmetadata', function onLoaded() {
          audio.currentTime = seekPosition;
          audio.removeEventListener('loadedmetadata', onLoaded);
        });
      }
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
      });
      
      setCurrentSong(item);
      setIsPlaying(true);
    }
  }, [volume]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    
    const handleEnded = () => {
      if (isAutoMode && currentPlaylist && currentPlaylist.length > 0) {
        // Auto-advance to next song in playlist (loop) - no fade, direct transition
        const nextIndex = (currentIndex + 1) % currentPlaylist.length;
        setCurrentIndex(nextIndex);
        playItemAtIndex(currentPlaylist, nextIndex);
      } else {
        setIsPlaying(false);
      }
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [isAutoMode, currentPlaylist, currentIndex, playItemAtIndex]);

  // Start auto-play with a playlist - supports seeking to current position for in-house radio
  // Fades out current audio before starting new playlist for smooth transitions
  const startAutoPlaylist = useCallback((playlist, startIndex = 0, seekPosition = 0) => {
    if (!playlist || playlist.length === 0) return;
    
    const startNewPlaylist = () => {
      setCurrentPlaylist(playlist);
      setCurrentIndex(startIndex);
      setIsAutoMode(true);
      playItemAtIndex(playlist, startIndex, seekPosition);
    };

    // If audio is currently playing, fade out first
    const audio = audioRef.current;
    if (audio && !audio.paused && isPlaying) {
      fadeOutAndExecute(startNewPlaylist, 400); // 400ms fade out
    } else {
      startNewPlaylist();
    }
  }, [playItemAtIndex, fadeOutAndExecute, isPlaying]);

  // Stop auto mode with fade out
  const stopAutoMode = useCallback(() => {
    const cleanup = () => {
      setIsAutoMode(false);
      setCurrentPlaylist(null);
      setCurrentIndex(0);
      setCurrentSong(null);
      if (audioRef.current) {
        audioRef.current.src = '';
      }
      setIsPlaying(false);
    };

    // Fade out if currently playing
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      fadeOutAndExecute(cleanup, 300);
    } else {
      cleanup();
    }
  }, [fadeOutAndExecute]);

  // Toggle mute (keeps playing but silences audio)
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
    } else {
      audio.muted = true;
      setIsMuted(true);
    }
  }, [isMuted]);

  // Play a single song (manual mode)
  const playSong = useCallback((song) => {
    setIsAutoMode(false);
    setCurrentPlaylist(null);
    
    const audio = audioRef.current;
    if (audio) {
      audio.src = song.file_path;
      audio.load();
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
      });
      setCurrentSong(song);
      setIsPlaying(true);
    }
  }, []);

  // Update volume
  const updateVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  }, []);

  // Skip to next song in auto mode
  const skipNext = useCallback(() => {
    if (!isAutoMode || !currentPlaylist || currentPlaylist.length === 0) return;
    
    const nextIndex = (currentIndex + 1) % currentPlaylist.length;
    setCurrentIndex(nextIndex);
    playItemAtIndex(currentPlaylist, nextIndex);
  }, [isAutoMode, currentPlaylist, currentIndex, playItemAtIndex]);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
    };
  }, []);

  const value = {
    // State
    currentSong,
    isPlaying,
    isMuted,
    volume,
    currentTime,
    duration,
    currentPlaylist,
    currentIndex,
    isAutoMode,
    
    // Actions
    playSong,
    toggleMute,
    updateVolume,
    startAutoPlaylist,
    stopAutoMode,
    skipNext,
    
    // Utilities
    formatTime,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerContext;
